import { FunctionComponent, useEffect, useState } from "react"
import { Alert, Col, Container, Form, Row } from "react-bootstrap"
import Button from "react-bootstrap/Button"
import { useNavigate, useParams } from "react-router-dom"
import { Agreement, Plan, RouterExtended, Tenant } from "../data/models"
import useLazyQuery from "../hooks/useLazyQuery"
import useQuery from "../hooks/useQuery"
import DateUtils from "../utils/DateUtils"

interface IScheduleStartPage {}
const ScheduleStartPage: FunctionComponent<IScheduleStartPage> = () => {
	const navigate = useNavigate()
	const params = useParams()
	const routerId = params.routerId
	const [tenant, setTenant] = useState<Tenant>()
	const [plan, setPlan] = useState<Plan>()
	const [date, setDate] = useState<string>("")
	const routerQuery = useQuery<void, RouterQueryResponse>(`${process.env.REACT_APP_KNIT_URL}routers?id=${routerId}`)
	const router = routerQuery.data?.routers?.[0]
	const tenantsQuery = useLazyQuery<void, TenantsQueryResponse>(`${process.env.REACT_APP_KNIT_URL}tenants?unitId=${router?.unitId}`)
	const plansQuery = useQuery<void, PlansQueryResponse>(`${process.env.REACT_APP_KNIT_URL}plans`)
	const agreementsUrl = `${process.env.REACT_APP_KNIT_URL}agreements`
	const postAgreementQuery = useLazyQuery<Partial<Agreement>, any>(agreementsUrl, "POST")
	//set default date based on router.currentEndDate
	useEffect(() => {
		if (!date && router?.currentEndDate) setDate(DateUtils.addDays(new Date(router?.currentEndDate), 1)!.toISOString().substring(0, 10))
	}, [date, router?.currentEndDate])
	//wait to query tenants until the router is loaded so we know what unitId to use
	useEffect(() => {
		if (!tenantsQuery.data && !tenantsQuery.loading && router?.unitId) tenantsQuery.query()
	}, [router?.unitId, tenantsQuery])
	//convert id to Tenant
	const setTenantId = (id: string) => {
		setTenant(tenantsQuery.data?.tenants.find((p) => p.id === Number(id)))
	}
	//convert id to Plan
	const setPlanId = (id: string) => {
		setPlan(plansQuery.data?.plans.find((p) => p.id === id))
	}
	//schedule start function
	const scheduleStart = async () => {
		await postAgreementQuery.query({
			body: {
				tenantId: tenant?.id,
				tenantName: tenant?.name,
				propertyId: router?.propertyId,
				unitId: router?.unitId,
				routerId: router?.id,
				planId: plan?.id,
				startDate: date,
			},
		})
		navigate(-1)
	}
	//
	const error = postAgreementQuery.error || routerQuery.error
	//
	if (!routerQuery.data) {
		return (
			<Container style={{ maxWidth: 500 }}>
				<h2 className="text-center mt-4">Start Internet Service</h2>
				<div className="text-center">Loading</div>
			</Container>
		)
	}
	return (
		<Container style={{ maxWidth: 500 }}>
			<h2 className="text-center mt-4">Start Internet Service</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<div className="text-center">
				Start internet service for {router?.propertyName} {router?.unitName} with the following details?
			</div>
			<Form.Group className="mb-3" controlId="plan">
				<Form.Label>Resident</Form.Label>
				<Form.Select required value={tenant?.id} onChange={(e) => setTenantId(e.target.value)}>
					<option></option>
					{tenantsQuery.data?.tenants?.map((tenant) => (
						<option key={tenant.id} value={tenant.id}>
							{tenant.name}
						</option>
					))}
				</Form.Select>
			</Form.Group>
			<Form.Group className="mb-3" controlId="plan">
				<Form.Label>Plan</Form.Label>
				<Form.Select required value={plan?.id} onChange={(e) => setPlanId(e.target.value)}>
					<option></option>
					{plansQuery.data?.plans?.map((plan) => (
						<option key={plan.id} value={plan.id}>
							{plan.name}
						</option>
					))}
				</Form.Select>
			</Form.Group>
			<Form.Group className="mb-3" controlId="date">
				<Form.Label>First Day of Service</Form.Label>
				<Form.Control type="date" required min={getMinStartDate(router?.currentEndDate)} value={date} onChange={(e) => setDate(e.target.value)} />
			</Form.Group>
			<Row className="mt-4">
				<Col xs="auto">
					<Button variant="secondary" disabled={postAgreementQuery.loading} onClick={() => navigate(-1)}>
						Cancel
					</Button>
				</Col>
				<Col></Col>
				<Col xs="auto">
					<Button variant="primary" disabled={postAgreementQuery.loading || !!error || !tenant || !plan || !date} onClick={scheduleStart}>
						{postAgreementQuery.loading ? "Saving" : "Confirm"}
					</Button>
				</Col>
			</Row>
		</Container>
	)
}

export default ScheduleStartPage

interface RouterQueryResponse {
	routers: RouterExtended[]
}
interface TenantsQueryResponse {
	tenants: Tenant[]
}
interface PlansQueryResponse {
	plans: Plan[]
}

const getMinStartDate = (currentEndDate?: string) => {
	const today = new Date().toISOString().substring(0, 10)
	if (currentEndDate == null) return today
	if (today > currentEndDate) return today
	return DateUtils.addDays(new Date(currentEndDate), 1)!.toISOString().substring(0, 10)
}
